/**
 * Pages ouvertes par un lien remis à un client.
 *
 * L'application n'a qu'une adresse : tout s'y navigue sans que l'URL change.
 * Le lien d'un client fait exception — il arrive de l'extérieur, par courriel
 * ou par message, et doit mener droit à sa pièce : `/p/<jeton>`.
 */

/**
 * Jeton porté par l'adresse, si elle est celle d'une page client.
 *
 * @param {string} chemin chemin de l'adresse (`window.location.pathname`)
 * @returns {string|null} le jeton, ou `null` si ce n'est pas une page client
 */
export function jetonDeLaPage(chemin) {
  const trouve = /^\/p\/([A-Za-z0-9_-]{20,100})\/?$/.exec(chemin || '');
  return trouve ? trouve[1] : null;
}

/**
 * Jeton porté par l'adresse, si elle est celle du bouton « Ouvrir … en PDF »
 * de l'e-mail d'envoi : `/p/<jeton>/pdf`.
 *
 * @param {string} chemin chemin de l'adresse (`window.location.pathname`)
 * @returns {string|null} le jeton, ou `null` si ce n'est pas ce bouton
 */
export function jetonDuPdf(chemin) {
  const trouve = /^\/p\/([A-Za-z0-9_-]{20,100})\/pdf\/?$/.exec(chemin || '');
  return trouve ? trouve[1] : null;
}

/**
 * Photo d'une offre ou d'une facture, touchée dans son PDF :
 * `/p/<jeton>/photo/<n>`.
 *
 * @param {string} chemin chemin de l'adresse (`window.location.pathname`)
 * @returns {{jeton: string, numero: number}|null} le jeton et le rang de la
 *   photo, ou `null` si ce n'est pas une photo
 */
export function photoDeLaPage(chemin) {
  const trouve = /^\/p\/([A-Za-z0-9_-]{20,100})\/photo\/(\d{1,3})\/?$/.exec(chemin || '');
  if (!trouve) return null;
  const numero = Number(trouve[2]);
  return numero >= 1 ? { jeton: trouve[1], numero } : null;
}

/**
 * Jeton porté par l'adresse, si elle est celle d'une invitation d'employé.
 *
 * L'invitation arrive aussi de l'extérieur, par message, et mène l'employé
 * — qui n'a pas encore d'accès — à la page où il choisit son code :
 * `/invitation/<jeton>`.
 *
 * @param {string} chemin chemin de l'adresse (`window.location.pathname`)
 * @returns {string|null} le jeton, ou `null` si ce n'est pas une invitation
 */
export function jetonDInvitation(chemin) {
  const trouve = /^\/invitation\/([A-Za-z0-9_-]{20,100})\/?$/.exec(chemin || '');
  return trouve ? trouve[1] : null;
}

/**
 * Jeton de l'invitation à l'espace client du magasin, porté par l'adresse.
 *
 * @param {string} chemin chemin de l'adresse
 * @returns {string|null} le jeton, ou null
 */
export function jetonDAccesClient(chemin) {
  const trouve = /^\/acces-client\/([A-Za-z0-9_-]{20,100})\/?$/.exec(chemin || '');
  return trouve ? trouve[1] : null;
}

/**
 * Date d'une pièce, telle qu'on l'écrit en Suisse.
 *
 * @param {string} iso date au format `AAAA-MM-JJ`
 * @returns {string} la date au format `JJ.MM.AAAA`, vide si elle manque
 */
export function dateSuisse(iso) {
  const trouve = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return trouve ? `${trouve[3]}.${trouve[2]}.${trouve[1]}` : '';
}

/**
 * Montant, avec l'apostrophe des milliers et deux décimales.
 *
 * @param {number} valeur montant
 * @returns {string} le montant écrit, `0.00` s'il manque
 */
export function montantSuisse(valeur) {
  const nombre = Number(valeur);
  const [entier, decimales] = (Number.isFinite(nombre) ? nombre : 0).toFixed(2).split('.');
  return entier.replace(/\B(?=(\d{3})+(?!\d))/g, "'") + '.' + decimales;
}

/**
 * Quantité, sans décimales inutiles : `20`, `1.5`.
 *
 * @param {number} valeur quantité
 * @returns {string} la quantité écrite
 */
export function quantiteLisible(valeur) {
  const nombre = Number(valeur);
  return Number.isFinite(nombre) ? String(+nombre.toFixed(3)) : '';
}

/** Clé de session : la photo en grand a été ouverte depuis la page du document. */
export const CLE_PHOTO_DEPUIS_LA_PAGE = 'swisstech.photoDepuisLaPage';
